import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface MorphSelectOption<T extends string = string> {
  value: T
  label: string
  description?: string
  disabled?: boolean
  icon?: ReactNode
  metadata?: Record<string, unknown>
  badge?: string
  color?: string
}

export interface MorphSelectProps<T extends string = string> {
  label?: string
  ariaLabel?: string
  value?: T | '' | null
  onChange: (value: T | null, option?: MorphSelectOption<T> | null) => void

  options?: MorphSelectOption<T>[]
  loadOptions?: (query: string, page: number) => Promise<MorphSelectOption<T>[]>
  pageSize?: number

  placeholder?: string
  searchPlaceholder?: string

  searchable?: boolean
  clearable?: boolean
  disabled?: boolean
  loading?: boolean

  emptyMessage?: string
  loadingMessage?: string
  errorMessage?: string
  emptyLabel?: string

  debounceMs?: number

  className?: string
  dropdownClassName?: string
  optionClassName?: string

  size?: 'sm' | 'md' | 'lg'
  menuPlacement?: 'down' | 'up'
  minMenuWidth?: number
}

const SIZE_STYLES = {
  sm: {
    trigger: 'min-h-[30px] px-2.5 text-[10px]',
    option: 'px-3 py-2',
    icon: 'h-2.5 w-2.5',
    badge: 'text-[9px]',
    itemHeight: 44,
  },
  md: {
    trigger: 'min-h-[34px] px-3 text-[11px]',
    option: 'px-3 py-2.5',
    icon: 'h-2.5 w-2.5',
    badge: 'text-[9px]',
    itemHeight: 48,
  },
  lg: {
    trigger: 'min-h-[38px] px-3.5 text-[12px]',
    option: 'px-3.5 py-3',
    icon: 'h-3 w-3',
    badge: 'text-[10px]',
    itemHeight: 52,
  },
} as const

function normalizeSearchValue(value: unknown): string {
  if (typeof value === 'string') return value.toLowerCase()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).toLowerCase()
  if (Array.isArray(value)) {
    return value
      .map((entry) => normalizeSearchValue(entry))
      .filter(Boolean)
      .join(' ')
  }
  if (value && typeof value === 'object') {
    return Object.values(value)
      .map((entry) => normalizeSearchValue(entry))
      .filter(Boolean)
      .join(' ')
  }
  return ''
}

function getSearchText<T extends string>(option: MorphSelectOption<T>): string {
  return [
    option.label,
    option.value,
    option.description,
    normalizeSearchValue(option.metadata),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function filterOptions<T extends string>(options: MorphSelectOption<T>[], query: string) {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return options
  return options.filter((option) => getSearchText(option).includes(normalizedQuery))
}

function mergeOptions<T extends string>(...collections: Array<Array<MorphSelectOption<T> | null | undefined> | undefined>) {
  const seen = new Set<string>()
  const merged: MorphSelectOption<T>[] = []

  collections.forEach((collection) => {
    collection?.forEach((option) => {
      if (!option || seen.has(option.value)) return
      seen.add(option.value)
      merged.push(option)
    })
  })

  return merged
}

function findNextEnabledIndex<T extends string>(
  options: MorphSelectOption<T>[],
  startIndex: number,
  step: 1 | -1,
) {
  if (!options.length) return -1

  for (let offset = 1; offset <= options.length; offset += 1) {
    const index = (startIndex + offset * step + options.length) % options.length
    if (!options[index]?.disabled) return index
  }

  return -1
}

function findInitialIndex<T extends string>(
  options: MorphSelectOption<T>[],
  selectedValue: T | '' | null | undefined,
) {
  const selectedIndex = selectedValue
    ? options.findIndex((option) => option.value === selectedValue && !option.disabled)
    : -1

  if (selectedIndex >= 0) return selectedIndex
  return options.findIndex((option) => !option.disabled)
}

export function MorphSelect<T extends string = string>({
  label,
  ariaLabel,
  value = null,
  onChange,
  options = [],
  loadOptions,
  pageSize,
  placeholder,
  searchPlaceholder = 'Buscar...',
  searchable = false,
  clearable = false,
  disabled = false,
  loading = false,
  emptyMessage,
  loadingMessage = 'Carregando...',
  errorMessage = 'Erro ao carregar opções',
  emptyLabel,
  debounceMs = 300,
  className,
  dropdownClassName,
  optionClassName,
  size = 'md',
  menuPlacement = 'down',
  minMenuWidth,
}: MorphSelectProps<T>) {
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [query, setQuery] = useState('')
  const [remoteOptions, setRemoteOptions] = useState<MorphSelectOption<T>[]>([])
  const [remoteError, setRemoteError] = useState(false)
  const [internalLoading, setInternalLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [cachedSelectedOption, setCachedSelectedOption] = useState<MorphSelectOption<T> | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const listScrollRef = useRef<HTMLDivElement | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const listId = useId()
  const searchInputId = useId()
  const requestIdRef = useRef(0)
  const [menuWidth, setMenuWidth] = useState<number | null>(null)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const styles = SIZE_STYLES[size]

  const mergedOptions = useMemo(
    () => mergeOptions(options, remoteOptions, cachedSelectedOption ? [cachedSelectedOption] : undefined),
    [cachedSelectedOption, options, remoteOptions],
  )

  const selectedOption = useMemo(
    () => mergedOptions.find((option) => option.value === value) ?? cachedSelectedOption ?? null,
    [cachedSelectedOption, mergedOptions, value],
  )

  const visibleOptions = useMemo(() => {
    if (loadOptions) return remoteOptions
    return filterOptions(options, query)
  }, [loadOptions, options, query, remoteOptions])

  const isLoading = loading || internalLoading
  const resolvedEmptyMessage = emptyMessage ?? emptyLabel ?? 'Nenhuma opção encontrada'
  const hasSelection = Boolean(selectedOption) || (typeof value === 'string' ? Boolean(value) : value != null)
  const triggerLabel = selectedOption?.label ?? (typeof value === 'string' && value ? value : placeholder ?? emptyLabel ?? '—')

  useEffect(() => {
    if (!value) {
      setCachedSelectedOption(null)
      return
    }

    const nextOption = mergedOptions.find((option) => option.value === value)
    if (nextOption) setCachedSelectedOption(nextOption)
  }, [mergedOptions, value])

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false)
      }
    }

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setRemoteError(false)
      setInternalLoading(false)
      setIsLoadingMore(false)
      setCurrentPage(1)
      setHasMore(false)
      requestIdRef.current += 1
      return
    }

    if (searchable) {
      window.setTimeout(() => searchInputRef.current?.focus(), 0)
    }
  }, [open, searchable])

  useEffect(() => {
    if (!open) return
    setHighlightedIndex(findInitialIndex(visibleOptions, value))
  }, [open, value, visibleOptions])

  useEffect(() => {
    const optionId = highlightedIndex >= 0 ? `${listId}-option-${highlightedIndex}` : null
    if (!open || !optionId) return

    const optionElement = document.getElementById(optionId)
    optionElement?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, listId, open])

  useEffect(() => {
    const updateWidth = () => {
      setMenuWidth(triggerRef.current?.offsetWidth ?? null)
    }

    updateWidth()

    const observer = typeof ResizeObserver === 'undefined' || !triggerRef.current
      ? null
      : new ResizeObserver(updateWidth)

    if (observer && triggerRef.current) {
      observer.observe(triggerRef.current)
    }

    window.addEventListener('resize', updateWidth)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', updateWidth)
    }
  }, [])

  useEffect(() => {
    if (!open) return

    const updateMenuPosition = () => {
      const trigger = triggerRef.current
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const width = Math.max(rect.width, minMenuWidth ?? 0)
      const left = Math.min(
        Math.max(8, rect.left),
        Math.max(8, window.innerWidth - width - 8),
      )
      const top = menuPlacement === 'up'
        ? rect.top - 6
        : rect.bottom + 6

      setMenuPosition({ top, left })
      setMenuWidth(width)
    }

    updateMenuPosition()

    window.addEventListener('resize', updateMenuPosition)
    window.addEventListener('scroll', updateMenuPosition, true)

    return () => {
      window.removeEventListener('resize', updateMenuPosition)
      window.removeEventListener('scroll', updateMenuPosition, true)
    }
  }, [open, menuPlacement, minMenuWidth])

  useEffect(() => {
    if (!loadOptions || !open) return

    const isFirstPage = currentPage === 1
    const currentRequestId = requestIdRef.current + 1
    requestIdRef.current = currentRequestId

    if (isFirstPage) {
      setInternalLoading(true)
      setRemoteOptions([])
      setRemoteError(false)
      setHasMore(false)
    } else {
      setIsLoadingMore(true)
    }

    const delay = isFirstPage ? Math.max(0, debounceMs) : 0

    const timeoutId = window.setTimeout(() => {
      void loadOptions(query, currentPage)
        .then((loadedOptions) => {
          if (requestIdRef.current !== currentRequestId) return
          if (isFirstPage) {
            setRemoteOptions(loadedOptions)
          } else {
            setRemoteOptions((prev) => mergeOptions(prev, loadedOptions))
          }
          if (pageSize !== undefined) {
            setHasMore(loadedOptions.length >= pageSize)
          }
        })
        .catch(() => {
          if (requestIdRef.current !== currentRequestId) return
          setRemoteError(true)
          if (isFirstPage) setRemoteOptions([])
        })
        .finally(() => {
          if (requestIdRef.current !== currentRequestId) return
          setInternalLoading(false)
          setIsLoadingMore(false)
        })
    }, delay)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [debounceMs, loadOptions, open, query, currentPage, pageSize])

  useEffect(() => {
    setCurrentPage(1)
  }, [query])

  useEffect(() => {
    if (!pageSize || !hasMore || !open || isLoadingMore || internalLoading) return
    if (!sentinelRef.current || !listScrollRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setCurrentPage((p) => p + 1)
        }
      },
      { root: listScrollRef.current, threshold: 0 },
    )

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [pageSize, hasMore, open, isLoadingMore, internalLoading])

  const closeMenu = () => {
    setOpen(false)
  }

  const selectOption = (option: MorphSelectOption<T>) => {
    if (option.disabled) return
    setCachedSelectedOption(option)
    onChange(option.value, option)
    closeMenu()
  }

  const clearSelection = () => {
    setCachedSelectedOption(null)
    setQuery('')
    setRemoteOptions([])
    setRemoteError(false)
    onChange(null, null)
    closeMenu()
  }

  const moveHighlight = (step: 1 | -1) => {
    if (!visibleOptions.length) return
    setHighlightedIndex((current) => {
      const startIndex = current >= 0 ? current : step === 1 ? -1 : 0
      return findNextEnabledIndex(visibleOptions, startIndex, step)
    })
  }

  const handleKeyboardSelection = (event: ReactKeyboardEvent<HTMLButtonElement | HTMLInputElement>) => {
    if (disabled) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!open) setOpen(true)
      moveHighlight(1)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) setOpen(true)
      moveHighlight(-1)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()

      if (!open) {
        setOpen(true)
        return
      }

      const option = highlightedIndex >= 0 ? visibleOptions[highlightedIndex] : null
      if (option && !option.disabled) {
        selectOption(option)
      }
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      closeMenu()
    }
  }

  const showStatusMessage = remoteError || isLoading || visibleOptions.length === 0

  return (
    <div ref={rootRef} className={`relative flex min-w-0 flex-col gap-1 ${className ?? ''}`.trim()}>
      {label ? (
        <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>
          {label}
        </span>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && highlightedIndex >= 0 ? `${listId}-option-${highlightedIndex}` : undefined}
        onClick={() => {
          if (!disabled) setOpen((current) => !current)
        }}
        onKeyDown={handleKeyboardSelection}
        className={`morph-select-trigger inline-flex w-full items-center justify-between gap-2 border pr-2 disabled:cursor-default disabled:opacity-45 morph-control ${styles.trigger}`.trim()}
        style={{
          borderColor: 'var(--border)',
          background: 'var(--bg-input)',
          color: 'var(--text)',
        }}
      >
        <span className="inline-flex min-w-0 flex-1 items-center gap-2">
          {selectedOption?.color ? (
            <span
              className={`${styles.icon} rounded-sm`}
              style={{ background: selectedOption.color }}
            />
          ) : null}
          {selectedOption?.icon}
          <span
            className="min-w-0 truncate text-left"
            style={hasSelection ? undefined : { color: 'var(--text-dim)' }}
          >
            {triggerLabel}
          </span>
        </span>
        <span className="flex items-center gap-1">
          {clearable && value ? (
            <span
              onClick={(event) => {
                event.stopPropagation()
                clearSelection()
              }}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] transition-colors"
              style={{ color: 'var(--text-dim)' }}
            >
              ×
            </span>
          ) : null}
          <span aria-hidden="true" className="text-[9px]" style={{ color: 'var(--text-dim)' }}>
            ▾
          </span>
        </span>
      </button>

      {open && menuPosition && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={menuRef}
            className={`fixed z-[1400] overflow-hidden rounded-xl border shadow-2xl ${dropdownClassName ?? ''}`.trim()}
            data-overlay-root="true"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              top: menuPlacement === 'up' ? 'auto' : menuPosition.top,
              bottom: menuPlacement === 'up' ? window.innerHeight - menuPosition.top : 'auto',
              left: menuPosition.left,
              width: menuWidth || '100%',
              background: 'var(--bg-popup)',
              borderColor: 'var(--border)',
            }}
          >
            {searchable ? (
              <div className="border-b p-2" style={{ borderColor: 'var(--border)' }}>
                <input
                  id={searchInputId}
                  ref={searchInputRef}
                  type="text"
                  role="combobox"
                  aria-label={ariaLabel ?? label ?? placeholder ?? 'Buscar opções'}
                  aria-expanded={open}
                  aria-controls={listId}
                  aria-activedescendant={highlightedIndex >= 0 ? `${listId}-option-${highlightedIndex}` : undefined}
                  value={query}
                  placeholder={searchPlaceholder}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={handleKeyboardSelection}
                  className="morph-input h-8 text-[11px]"
                />
              </div>
            ) : null}

            <div id={listId} role="listbox" aria-label={ariaLabel ?? label ?? placeholder}>
              <div
                ref={listScrollRef}
                className="overflow-y-auto p-1.5"
                style={{
                  maxHeight: (pageSize ? Math.min(pageSize - 1, 5) : 5) * styles.itemHeight,
                  scrollbarGutter: 'auto',
                }}
              >
                {showStatusMessage ? (
                  <div className="px-3 py-3 text-[11px]" style={{ color: 'var(--text-dim)' }}>
                    {remoteError ? errorMessage : isLoading ? loadingMessage : resolvedEmptyMessage}
                  </div>
                ) : (
                  <>
                    {visibleOptions.map((option, index) => {
                      const active = index === highlightedIndex
                      const selected = option.value === value
                      return (
                        <button
                          key={option.value}
                          id={`${listId}-option-${index}`}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          disabled={option.disabled}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => selectOption(option)}
                          className={`mb-1 flex w-full min-w-0 items-center gap-2 rounded-lg border border-transparent text-left disabled:opacity-45 ${styles.option} ${optionClassName ?? ''}`.trim()}
                          style={{
                            background: selected
                              ? 'rgba(99,102,241,.14)'
                              : active
                                ? 'rgba(99,102,241,.08)'
                                : 'transparent',
                            borderColor: selected
                              ? 'rgba(99,102,241,.26)'
                              : active
                                ? 'rgba(99,102,241,.18)'
                                : 'transparent',
                            color: 'var(--text)',
                          }}
                        >
                          {option.color ? (
                            <span className={`${styles.icon} rounded-sm`} style={{ background: option.color }} />
                          ) : null}
                          {option.icon}
                          <span className="min-w-0 flex-1 leading-tight">
                            <span className="block truncate text-[10px]">{option.label}</span>
                            {option.description ? (
                              <span className="mt-1 block text-[9px]" style={{ color: 'var(--text-dim)' }}>
                                {option.description}
                              </span>
                            ) : null}
                          </span>
                          {option.badge ? (
                            <span
                              className={`rounded-full border px-1.5 py-0.5 leading-none ${styles.badge}`}
                              style={{ borderColor: 'var(--border)' }}
                            >
                              {option.badge}
                            </span>
                          ) : null}
                        </button>
                      )
                    })}
                    {pageSize ? (
                      <div ref={sentinelRef} className="flex items-center justify-center py-1.5">
                        {isLoadingMore ? (
                          <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>
                            {loadingMessage}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  )
}
